import { StandardEvents } from '@shopify/events';

class KenzeFloatingCart extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a');
    this.count = this.querySelector('.kenze-floating-cart__count');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.link.addEventListener('click', this.openCart, options);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, options);
    window.addEventListener('resize', this.scheduleAlignment, options);
    // Refresh after scrolling or Safari's visible viewport changes, not just page load.
    window.addEventListener('scroll', this.scheduleAlignment, { ...options, passive: true, capture: true });
    window.visualViewport?.addEventListener('resize', this.scheduleAlignment, options);
    window.visualViewport?.addEventListener('scroll', this.scheduleAlignment, options);
    window.addEventListener('pageshow', this.onPageShow, options);
    this.resizeObserver = new ResizeObserver(this.scheduleAlignment);
    // Discover late-loading Inbox embeds without polling or watching cart text changes.
    this.observer = new MutationObserver((records) => {
      if (records.some((record) => record.type === 'attributes' || [...record.addedNodes].some((node) =>
        node instanceof Element && (node.matches(this.chatSelector + ', [part~="activator"]') || node.querySelector(this.chatSelector + ', [part~="activator"]'))
      ))) this.scheduleAlignment();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    customElements.whenDefined('shopify-chat').then(() => {
      if (this.isConnected) this.scheduleAlignment();
    });
    this.scheduleAlignment();
  }

  disconnectedCallback() {
    this.controller.abort();
    this.observer.disconnect();
    this.resizeObserver.disconnect();
    cancelAnimationFrame(this.frame);
    this.animation?.cancel();
  }

  chatSelector = 'shopify-chat, #ShopifyChat, #shopify-chat, .shopify-chat-button, iframe[title*="chat" i]';

  openCart = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (typeof drawer?.open !== 'function') return; // Keep the real cart URL as a fallback.
    event.preventDefault();
    drawer.open();
  };

  onCartUpdate = (event) => {
    event.promise?.then(({ cart, detail }) => {
      if (!this.isConnected || detail?.didError) return;
      const quantity = cart?.totalQuantity ?? detail?.itemCount;
      if (Number.isFinite(quantity)) this.renderCount(quantity);
      if (event.action === 'add' && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.animation?.cancel();
        this.animation = this.link.animate([
          { transform: 'scale(1) rotate(0)' },
          { transform: 'scale(1.15) rotate(-10deg)', offset: 0.35 },
          { transform: 'scale(1.08) rotate(8deg)', offset: 0.65 },
          { transform: 'scale(1) rotate(0)' },
        ], { duration: 420, easing: 'ease-out' });
      }
    }).catch(() => { /* The native cart owns error messages; do not celebrate failed adds. */ });
  };

  renderCount(quantity) {
    this.count.textContent = quantity > 99 ? '99+' : String(quantity);
    this.count.hidden = quantity === 0;
  }

  onPageShow = (event) => {
    this.scheduleAlignment();
    if (!event.persisted) return;
    // Only revalidate after back/forward cache restoration; normal updates reuse their response.
    fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: this.controller.signal })
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((cart) => {
        this.renderCount(cart.item_count);
        document.querySelectorAll('cart-icon').forEach((icon) => icon.renderCartBubble?.(cart.item_count, false));
      }).catch(() => {});
  };

  scheduleAlignment = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      if (matchMedia('(max-width: 989px)').matches) {
        // Only measure Shopify's actual launcher, never its host or other chat buttons.
        const chat = document.querySelector('shopify-chat');
        if (chat) {
          this.resizeObserver.observe(chat);
          this.observer.observe(chat, { attributes: true, attributeFilter: ['open', 'mode'] });
          if (chat.shadowRoot) this.observer.observe(chat.shadowRoot, { childList: true, subtree: true });
        }
        const launcher = chat?.shadowRoot?.querySelector('[part~="activator"]');
        if (launcher && !chat.hasAttribute('open')) {
          this.resizeObserver.observe(launcher);
          const target = launcher.getBoundingClientRect();
          const cart = this.link.getBoundingClientRect();
          if (target.width >= 40 && target.height >= 30 && target.height <= 90 &&
              target.top >= 0 && target.bottom <= innerHeight + 1 && cart.height > 0) {
            // Use a relative correction in the same coordinate space. This avoids
            // mixing Safari's visual-viewport and layout-viewport heights.
            const correction = (cart.top + cart.height / 2) - (target.top + target.height / 2);
            const bottom = parseFloat(getComputedStyle(this.link).bottom);
            if (Number.isFinite(bottom) && Math.abs(correction) > 0.5) {
              this.style.setProperty('--kenze-mobile-cart-bottom', `${Math.max(0, bottom + correction)}px`);
            }
          }
        }
        return;
      }
      this.style.removeProperty('--kenze-mobile-cart-bottom');
      for (const host of document.querySelectorAll(this.chatSelector)) {
        this.resizeObserver.observe(host);
        // Open shadow roots expose native launchers; cross-origin iframes expose only their box.
        const candidates = host.shadowRoot ? [...host.shadowRoot.querySelectorAll('button'), host] : [host];
        for (const candidate of candidates) {
          const rect = candidate.getBoundingClientRect();
          if (rect.width < 40 || rect.width > 260 || rect.height < 40 || rect.height > 90 || rect.top < innerHeight / 2 || rect.bottom > innerHeight) continue;
          this.style.setProperty('--kenze-chat-bottom', `${innerHeight - rect.bottom}px`);
          this.style.setProperty('--kenze-chat-size', `${rect.height}px`);
          return;
        }
      }
    });
  };
}

if (!customElements.get('kenze-floating-cart')) customElements.define('kenze-floating-cart', KenzeFloatingCart);
